import { getApiBaseUrl } from '../config/api.config'
import { getTokenFromUrl } from '../utils/auth'

import type { EnvironmentWithChina } from '../App'

export class AlterationsApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
  ) {
    super(message)
    this.name = 'AlterationsApiError'
  }
}

export interface AlterationAdjustment {
  adjustmentOptionId: string | null
  exactAdjustment: number
}

/** The fields of the Alterations API `AlterationDto` the wardrobe reads. */
export interface Alteration {
  status: string | null
  finishDateTime: string | null
  alterationAdjustments: AlterationAdjustment[] | null
}

export interface AdjustmentOption {
  id: string
  descriptionLocalized: Record<string, string> | null
}

interface AlterationsSearchResponse {
  data: Alteration[] | null
}

// An EPC has a handful of alteration sets at most; one generous page keeps the
// latest `Finished` one from being cut off.
const SEARCH_PAGE_SIZE = 50

async function apiRequest<T>(
  path: string,
  env: EnvironmentWithChina,
  authToken: string | undefined,
  init: { method: 'GET' | 'POST'; body?: unknown },
): Promise<T> {
  const token = authToken || getTokenFromUrl()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(`${getApiBaseUrl(env)}${path}`, {
    method: init.method,
    headers,
    credentials: 'omit',
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  })

  if (!response.ok) {
    throw new AlterationsApiError(
      `Alterations request failed: ${path}`,
      response.status,
    )
  }

  return response.json() as Promise<T>
}

export async function fetchFinishedAlterations(
  epc: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<Alteration[]> {
  const result = await apiRequest<AlterationsSearchResponse>(
    '/public/alterationsapi/alterations/api/v1/Alterations/search',
    env,
    authToken,
    {
      method: 'POST',
      body: {
        epc,
        pagination: { pageNumber: 1, pageSize: SEARCH_PAGE_SIZE },
      },
    },
  )

  return result.data ?? []
}

export function fetchAdjustmentOptions(
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<AdjustmentOption[]> {
  return apiRequest<AdjustmentOption[]>(
    '/public/alterationsapi/adjustmentoptions/api/AdjustmentOption',
    env,
    authToken,
    { method: 'GET' },
  )
}
